/**
 * Rental data integrity helpers — focused on the optional "Second Driver".
 *
 * Rule: second-driver fields may ONLY hold data when `hasDriver2` is explicitly
 * enabled. When it is off, every driver2 field is force-cleared so no stale or
 * placeholder data can ever be entered, saved, or printed.
 *
 * Used on BOTH sides:
 *   - front-end (RentalForm submit) for UX/instant feedback
 *   - "back-end" guard (handleSave, right before the Supabase write)
 */

export const DRIVER2_FIELDS = [
  'driver2Id',
  'driver2Name',
  'driver2Cin',
  'driver2Permis',
  'driver2PermisDate',
  'driver2Phone',
];

/**
 * Returns a NEW object with second-driver fields normalized.
 * - hasDriver2 falsy  -> hasDriver2:false + all driver2 fields emptied.
 * - hasDriver2 truthy -> driver2 fields trimmed.
 */
export const sanitizeDriver2 = (data = {}) => {
  const out = { ...data };
  if (!out.hasDriver2) {
    out.hasDriver2 = false;
    for (const f of DRIVER2_FIELDS) out[f] = '';
    return out;
  }
  for (const f of DRIVER2_FIELDS) {
    if (typeof out[f] === 'string') out[f] = out[f].trim();
  }
  return out;
};

/**
 * Validate second-driver consistency.
 * @returns {{ valid: boolean, error: string|null }}
 */
export const validateDriver2 = (data = {}, isAr = false) => {
  // Disabled: any leftover data is a logic error (guarded by sanitize, but
  // we still reject so a malformed payload can never slip through).
  if (!data.hasDriver2) {
    const hasLeftover = DRIVER2_FIELDS.some((f) => data[f] && String(data[f]).trim() !== '');
    if (hasLeftover) {
      return {
        valid: false,
        error: isAr
          ? 'لا يمكن حفظ بيانات سائق إضافي بدون تفعيل خيار "سائق إضافي".'
          : 'Impossible d’enregistrer des données de 2ᵉ chauffeur sans activer l’option.',
      };
    }
    return { valid: true, error: null };
  }

  // Enabled: a name is the minimum required identification.
  if (!data.driver2Name || String(data.driver2Name).trim() === '') {
    return {
      valid: false,
      error: isAr
        ? 'يرجى إدخال اسم السائق الإضافي أو إلغاء تفعيل الخيار.'
        : 'Veuillez saisir le nom du 2ᵉ chauffeur ou désactiver l’option.',
    };
  }
  return { valid: true, error: null };
};

/**
 * One-shot guard: sanitize then validate. Throws on invalid (used by the
 * save path so nothing invalid can reach the database).
 */
export const enforceDriver2 = (data = {}, isAr = false) => {
  const clean = sanitizeDriver2(data);
  const { valid, error } = validateDriver2(clean, isAr);
  if (!valid) throw new Error(error);
  return clean;
};
